import React from "react";
import { Modal, Button, Progress, Tag, Space, Steps, Switch } from "antd";
import {
  PlayCircleOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  TruckOutlined,
  EnvironmentOutlined,
  ScanOutlined,
} from "@ant-design/icons";
import { BookingInfo } from "../../types/container-unloading";
import "./ContainerOverview.css";

const { Step } = Steps;

interface ContainerOverviewProps {
  visible: boolean;
  onClose: () => void;
  containerReference: string;
  bookings: BookingInfo[];
  isUnloadingInProgress: boolean;
  isUnloadingCompleted: boolean;
  canStartUnloading: boolean;
  startingUnload: boolean;
  completedBookings: number;
  totalBookings: number;
  progressPercent: number;
  onStartUnloading: () => void;
  onResetContainer: () => void;
  autoScanEnabled?: boolean;
  onToggleAutoScan?: () => void;
}

export const ContainerOverview: React.FC<ContainerOverviewProps> = ({
  visible,
  onClose,
  containerReference,
  bookings,
  isUnloadingInProgress,
  isUnloadingCompleted,
  canStartUnloading,
  startingUnload,
  completedBookings,
  totalBookings,
  progressPercent,
  onStartUnloading,
  onResetContainer,
  autoScanEnabled = false,
  onToggleAutoScan,
}) => {
  // Calculate totals
  const totalProducts = bookings.reduce((sum, b) => sum + b.products.length, 0);
  const scannedProducts = bookings.reduce(
    (sum, b) => sum + b.products.filter((p) => p.completed).length,
    0
  );

  return (
    <Modal
      className="container-overview-modal"
      title={
        <div className="overview-title">📦 {containerReference} - Overview</div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width="100%"
    >
      <div className="overview-content">
        {/* Status and Actions */}
        <div className="overview-section">
          <div className="section-title">Container Status</div>
          <Tag
            color={
              isUnloadingCompleted
                ? "success"
                : isUnloadingInProgress
                ? "processing"
                : "default"
            }
            style={{ fontSize: "14px", padding: "6px 12px" }}
          >
            {isUnloadingCompleted
              ? "✅ Completed"
              : isUnloadingInProgress
              ? "🔄 In Progress"
              : "⏸️ Not Started"}
          </Tag>

          {/* Container Workflow Steps */}
          {isUnloadingInProgress && (
            <div style={{ marginTop: 16, marginBottom: 16 }}>
              <Steps size="small">
                <Step title="Count" icon={<CheckCircleOutlined />} />
                <Step title="Delivery" icon={<TruckOutlined />} />
                <Step title="Ship" icon={<EnvironmentOutlined />} />
              </Steps>
            </div>
          )}

          {/* Progress */}
          <div style={{ marginTop: 16 }}>
            <Progress
              percent={Math.round(progressPercent)}
              status={progressPercent === 100 ? "success" : "active"}
              strokeColor={{
                "0%": "#1890ff",
                "100%": "#52c41a",
              }}
            />
            <div className="progress-details">
              <div>
                📦 {scannedProducts}/{totalProducts} products scanned
              </div>
              <div>
                📋 {completedBookings}/{totalBookings} bookings completed
              </div>
            </div>
          </div>

          {/* Zebra Scanner Toggle */}
          {isUnloadingInProgress && onToggleAutoScan && (
            <div
              style={{
                marginTop: 16,
                marginBottom: 16,
                padding: "12px",
                backgroundColor: autoScanEnabled ? "#f6ffed" : "#fafafa",
                borderRadius: 8,
                border: `2px solid ${autoScanEnabled ? "#52c41a" : "#d9d9d9"}`,
              }}
            >
              <Space
                size="middle"
                style={{ width: "100%", justifyContent: "space-between" }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      marginBottom: 4,
                      color: autoScanEnabled ? "#52c41a" : "#000",
                    }}
                  >
                    <ScanOutlined
                      style={{
                        marginRight: 8,
                        color: autoScanEnabled ? "#52c41a" : "#8c8c8c",
                      }}
                    />
                    Zebra Scanner
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "#666",
                    }}
                  >
                    {autoScanEnabled
                      ? "🟢 Active - Scan to auto-count products"
                      : "⚪ Inactive - Tap to enable"}
                  </div>
                </div>
                <Switch
                  checked={autoScanEnabled}
                  onChange={onToggleAutoScan}
                  size="default"
                  style={{
                    backgroundColor: autoScanEnabled ? "#52c41a" : undefined,
                  }}
                />
              </Space>
            </div>
          )}

          {/* Action Buttons */}
          <div className="action-buttons">
            {canStartUnloading && (
              <Button
                type="primary"
                size="large"
                block
                icon={<PlayCircleOutlined />}
                onClick={onStartUnloading}
                loading={startingUnload}
                style={{
                  backgroundColor: "#52c41a",
                  borderColor: "#52c41a",
                  height: 52,
                  fontSize: 16,
                  fontWeight: 600,
                }}
              >
                Start Unloading
              </Button>
            )}
            {isUnloadingInProgress && (
              <Button
                danger
                size="large"
                block
                icon={<ReloadOutlined />}
                onClick={onResetContainer}
                style={{ height: 52, fontSize: 16, fontWeight: 600 }}
              >
                Reset Container
              </Button>
            )}
          </div>
        </div>

        {/* Compact Product Overview */}
        <div className="overview-section">
          <div className="section-title">
            Products Overview ({scannedProducts}/{totalProducts})
          </div>

          <div className="products-overview">
            {bookings.map((booking) => (
              <div key={booking.id} className="booking-overview">
                <div className="booking-header-compact">
                  <span className="booking-ref">
                    {booking.booking_reference}
                  </span>
                  <span className="booking-company">
                    {booking.company_name}
                  </span>
                  {booking.has_delivery_address ? (
                    <Tag color="blue" style={{ fontSize: "10px" }}>
                      🚚
                    </Tag>
                  ) : (
                    <Tag color="orange" style={{ fontSize: "10px" }}>
                      📦
                    </Tag>
                  )}
                </div>

                <div className="products-grid-compact">
                  {booking.products.map((product) => (
                    <div
                      key={product.id}
                      className={`product-chip ${
                        product.completed ? "completed" : "pending"
                      }`}
                    >
                      {product.completed ? (
                        <CheckCircleOutlined className="product-icon success" />
                      ) : (
                        <CloseCircleOutlined className="product-icon pending" />
                      )}
                      <span className="product-name-short">
                        {product.name}
                        {product.ean && (
                          <span className="product-ean-suffix">
                            {" "}
                            ..{product.ean.toString().slice(-4)}
                          </span>
                        )}
                      </span>
                      <span className="product-count">
                        {product.actual_cartons || 0}/
                        {product.estimated_cartons}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="overview-section">
          <div className="quick-stats">
            <div className="stat-item">
              <div className="stat-value">
                {bookings.filter((b) => b.delivery_type === "delivery").length}
              </div>
              <div className="stat-label">🚚 Delivery</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">
                {bookings.filter((b) => b.delivery_type === "storage").length}
              </div>
              <div className="stat-label">📦 Storage</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{totalBookings}</div>
              <div className="stat-label">Total Bookings</div>
            </div>
          </div>
        </div>

        {/* Close Button */}
        <Button
          size="large"
          block
          onClick={onClose}
          style={{ marginTop: 16, height: 48 }}
        >
          Close
        </Button>
      </div>
    </Modal>
  );
};
